/**
 * Soft≠Live Page164 — SA Tenants residual liquid (page81/127 buses + status).
 * Mount: /sa/tenant
 * Buses:
 *   GET /sa/tenant/status (always-on Live posture)
 *   GET /auth/admins · POST /auth/admins/{u}/close
 *   POST /auth/users/{u}/sessions/revoke
 * Soft residual deleted:
 *  - sandpit list without liquid rail / KPI / Re-read / status SoT
 *  - Soft invent device/seat/plan counts (stays blank — no CAP bus)
 *  - Soft missing-close-API theatre
 *  - Soft invent close when seat gated
 */
import { useMemo, useState } from "react";
import { html, F, Ico, Input, Field, Card, Alert, Ticker, KV, go } from "./ui.js";
import { path, useAction, useApi } from "./api.js";
import { envelopeSummary } from "./action-envelope.js";
import { useSession } from "./session.js";
import { Unavailable } from "./sa-shared.js";

/** Pure: client guess read — Live status wins when present. */
export function canReadTenantsOf(me) {
  if (!me) return false;
  const r = String(me.role || "");
  if (r === "superadmin") return true;
  if (r !== "sa_staff") return false;
  const caps = me.caps || me.sa_capabilities || [];
  const list = Array.isArray(caps) ? caps : String(caps || "").split(",");
  return list.some((c) => /sa_user_admin|sa_full_access|sa_full/i.test(String(c || "").trim()));
}

/** Pure: close seat — superadmin or sa_user_admin. */
export function canCloseTenantOf(me) {
  if (!me) return false;
  if (String(me.role || "") === "superadmin") return true;
  const caps = me.caps || me.sa_capabilities || [];
  const list = Array.isArray(caps) ? caps : String(caps || "").split(",");
  return list.some((c) => /sa_user_admin|sa_full_access|sa_full/i.test(String(c || "").trim()));
}

/** Pure: admin row from Live pack — never invent Soft email/seats. */
export function tenantAdminOf(row) {
  if (!row || typeof row !== "object") return null;
  const username = String(row.username || "").trim();
  if (!username) return null;
  return {
    username,
    email: String(row.email || "").trim() || "—",
    status: String(row.status || "").trim() || "—",
  };
}

/** Pure: Live admin register. */
export function tenantRegisterOf(pack) {
  if (!pack) return [];
  const items = pack.items || pack.admins || (Array.isArray(pack) ? pack : null);
  if (!Array.isArray(items)) return [];
  return items.map(tenantAdminOf).filter(Boolean);
}

/** Pure: KPI strip — blanks until Live (no Soft device/seat invent). */
export function tenantKpisOf(rows) {
  const list = Array.isArray(rows) ? rows : [];
  return {
    admins: list.length,
    withEmail: list.filter((r) => r && r.email && r.email !== "—").length,
    devices: null, // no singular CAP bus
    seats: null,
  };
}

export function SaTenantsDesk({ toast }) {
  const { me } = useSession();
  const posture = useApi(path("get__sa_tenant_status"));
  // Fail-closed while loading — Soft invent open list/close deleted.
  const post = posture.loading || !posture.data ? {} : (posture.data || {});
  const canRead = post.list_allowed === true;
  const canClose = post.close_allowed === true;
  const detail = post.detail || null;
  const r = useApi(canRead ? path("get__auth_admins") : "");
  const { run, busy, error, envelope } = useAction();
  const admins = useMemo(() => tenantRegisterOf(r.data), [r.data]);
  const kpis = useMemo(() => tenantKpisOf(admins), [admins]);
  const denied = !!(r.error && /\b40[13]\b|forbidden|insufficient|capability/i.test(String(r.error)));

  const [closeUser, setCloseUser] = useState("");
  const [confirm, setConfirm] = useState("");
  const [transferTo, setTransferTo] = useState("");

  const reRead = () => {
    posture.reload && posture.reload();
    r.reload && r.reload();
    toast && toast("Tenant posture + admins re-read", "go");
  };

  const endSessions = async (username) => {
    const name = String(username || "").trim();
    if (!name) return;
    const out = await run(path("post__auth_users_p_sessions_revoke", { pathParams: [name] }));
    if (!out) {
      toast && toast("Could not end sessions refuse · " + name, "bad");
      return;
    }
    toast && toast("Sessions ended · " + name, "warn");
  };

  const closeTenant = async () => {
    if (!canClose) {
      toast && toast("Close tenant is not available on this seat.", "warn");
      return;
    }
    const u = closeUser.trim();
    if (!u) { toast && toast("Administrator username required", "warn"); return; }
    if (confirm.trim() !== "CLOSE TENANT") {
      toast && toast("Type CLOSE TENANT exactly to confirm", "warn");
      return;
    }
    const body = { confirm_text: "CLOSE TENANT" };
    if (transferTo.trim()) body.transfer_devices_to = transferTo.trim();
    const out = await run(path("post__auth_admins_p_close", { pathParams: [u] }), {
      method: "POST",
      body,
    });
    if (!out) {
      toast && toast(error || "Close tenant refused — nothing was changed", "bad");
      return;
    }
    toast && toast("Tenant closed · " + u, "warn");
    setCloseUser("");
    setConfirm("");
    setTransferTo("");
    r.reload && r.reload();
  };

  if (!canRead) {
    return html`<div className="sa-tenants-desk sa-tenants-desk--liquid"
      data-cmc-page="page127-sa-tenants" data-cmc-legacy="page81-sa-tenants"
      data-cmc-residual="page164-tenants">
      <div className="sa-tenants-desk__rail">
        <div>
          <p className="eyebrow">Platform · Tenants</p>
          <h2 className="sa-tenants-desk__title">Seat closed</h2>
          <p className="note">Administrator list stays empty until this seat opens.</p>
        </div>
        <div className="btn-row sa-tenants-desk__acts">
          <button type="button" className="btn btn--ghost btn--sm" onClick=${reRead}
            disabled=${posture.loading}>Re-read posture</button>
          <button type="button" className="btn btn--ghost btn--sm"
            onClick=${() => go("/sa/custody")}>Custody</button>
          <button type="button" className="btn btn--ghost btn--sm"
            onClick=${() => go("/sa/team")}>Staff</button>
        </div>
      </div>
      <${Card} title="Tenants posture" meta="Live">
        ${posture.loading ? html`<p className="note">Reading posture…</p>` : null}
        ${posture.error ? html`<${Alert} tone="warn" title="Posture refused"
          say=${String(posture.error)}/>` : null}
        ${!posture.loading && !posture.error ? html`
          <p className="note">
            List and close sealed
            · ${detail || "not available on this account"}
          </p>` : null}
      <//>
      <${Alert} tone="info" title="Tenant list not on this account"
        say=${"Tenant admins list needs superadmin or staff user-admin. This account is "
          + (me && me.role ? me.role : "operator")
          + " — brochure tenants are not invented."}/>
      <${Card} title="Administrators (tenants)" meta="gated">
        <p className="note">No admin rows are offered on this grant.</p>
      <//>
    </div>`;
  }

  return html`<div className="sa-tenants-desk sa-tenants-desk--liquid"
    data-cmc-page="page127-sa-tenants" data-cmc-legacy="page81-sa-tenants"
    data-cmc-residual="page164-tenants">
    <div className="sa-tenants-desk__rail">
      <div>
        <p className="eyebrow">Platform · Tenants</p>
        <h2 className="sa-tenants-desk__title">Administrators</h2>
        <p className="note">
          High-speed tenant buses. Soft missing-close-API theatre deleted.
          Device/seat/plan counts stay off until a singular CAP metrics bus exists.
          Posture only.
        </p>
      </div>
      <div className="btn-row sa-tenants-desk__acts">
        <button type="button" className="btn btn--ghost btn--sm" onClick=${reRead}
          disabled=${r.loading || posture.loading}>
          <${Ico} d="refresh" w=${14}/> Re-read
        </button>
        <button type="button" className="btn btn--ghost btn--sm"
          onClick=${() => go("/sa/custody")}>Custody</button>
        <button type="button" className="btn btn--ghost btn--sm"
          onClick=${() => go("/sa/team")}>Staff</button>
      </div>
    </div>

    <${Card} title="Tenants posture" meta="Live">
      ${posture.loading ? html`<p className="note">Reading posture…</p>` : null}
      ${posture.error ? html`<${Alert} tone="warn" title="Posture refused"
        say=${String(posture.error)}/>` : null}
      ${!posture.loading && !posture.error ? html`
        <div className="sa-tenants-desk__kpis" role="group" aria-label="Tenants posture">
          ${[
            ["Seat", canRead ? "open" : "gated", canRead ? "1" : "0", "status Live"],
            ["Close", canClose ? "armed" : "—", canClose ? "1" : "0", "POST close"],
            ["CAP metrics", "none", "0", "honest blank"],
            ["List", canRead ? "open" : "sealed", canRead ? "1" : "0", "admins"],
          ].map(([k, v, on, em]) => html`
            <div key=${k} className="sa-tenants-desk__kpi" data-on=${on}>
              <span>${k}</span>
              <b>${v}</b>
              <em>${em}</em>
            </div>`)}
        </div>
        <p className="note">
          list ${post.list_path || "/auth/admins"}
          · close template ${post.close_path_template || "/auth/admins/{username}/close"}
         
        </p>` : null}
    <//>

    <${Alert} tone="info" title="Tenants desk"
      say="Administrators appear when the list seat is open. Ending sessions and closing a tenant only succeed after the server accepts the write."/>
    ${envelope ? html`<${KV} k="Last action" v=${envelopeSummary(envelope) || "—"} tone=${envelope.ok ? "hi" : "warn"}/>` : null}

    ${error ? html`<${Alert} tone="warn" title="Last write failed" say=${error}/>` : null}
    ${r.error ? html`
      <${Alert} tone=${denied ? "warn" : "bad"} title=${denied ? "Admin list gated" : "Admin list read failed"}
        say=${String(r.error)}/>` : null}

    <div className="sa-tenants-desk__kpis" role="group" aria-label="Tenant KPIs">
      ${[
        ["Admins", kpis.admins, kpis.admins ? "1" : "0", "Register"],
        ["With email", kpis.withEmail, kpis.withEmail ? "1" : "0", "from Live"],
        ["Devices", "—", "0", "no CAP bus"],
        ["Seats/plans", "—", "0", "no invent"],
      ].map(([k, v, on, em]) => html`
        <div key=${k} className="sa-tenants-desk__kpi" data-on=${on}>
          <span>${k}</span>
          <b>${typeof v === "number" ? html`<${Ticker} value=${v}/>` : v}</b>
          <em>${em}</em>
        </div>`)}
    </div>

    <${Card} title="Administrators (tenants)"
        meta=${admins.length ? admins.length + " accounts" : "Empty or gated"}
        sub="Under the single root SA, the next layer is admin only. There is no second SA to register. Exactly one root SA.">
      ${!admins.length ? html`<${Unavailable} r=${r} what="the administrator list"/>` : html`
      <div className="sa-tenants-desk__table-wrap">
        <table className="tbl">
          <thead><tr><th>Administrator</th><th>Email</th><th>Sessions</th><th>Close</th></tr></thead>
          <tbody>${admins.map((t) => html`
            <tr key=${t.username}>
              <td><b>${t.username}</b></td>
              <td><span className="m">${t.email}</span></td>
              <td><button className="btn btn--danger btn--xs" type="button" disabled=${busy}
                    onClick=${() => endSessions(t.username)}>End sessions</button></td>
              <td><button className="btn btn--ghost btn--xs" type="button"
                    disabled=${!canClose}
                    onClick=${() => { setCloseUser(t.username); setConfirm(""); }}>
                    Prefill close</button></td>
            </tr>`)}</tbody>
        </table>
      </div>`}
      <p className="note">Device / seat / plan counts stay off this desk — no singular CAP/tenant metrics bus reports them per admin. Soft joining of four surfaces is forbidden. Not a second SA.</p>
    <//>

    <${Card} title="Close tenant" meta="Close" cls="mt"
        sub=${canClose
          ? "Superadmin / sa_user_admin. Confirm text must be exactly CLOSE TENANT. Optional transfer keeps devices under another admin instead of unclaim."
          : "Close needs a platform admin seat that arms close."}>
      ${!canClose ? html`<p className="note">Close form stays off this grant. Success is forbidden without server confirmation.</p>` : html`
        <${Field} label="Administrator username">
          <${Input} icon="user" value=${closeUser} onChange=${(e) => setCloseUser(e.target.value)}/>
        <//>
        <${Field} label="Confirm" hint="Type CLOSE TENANT exactly">
          <${Input} icon="alert" value=${confirm} onChange=${(e) => setConfirm(e.target.value)}
            placeholder="CLOSE TENANT"/>
        <//>
        <${Field} label="Transfer devices to (optional)">
          <${Input} icon="user" value=${transferTo} onChange=${(e) => setTransferTo(e.target.value)}
            placeholder="another.admin"/>
        <//>
        <button className="btn btn--danger btn--sm" disabled=${busy || !closeUser.trim()}
          onClick=${closeTenant}>Close tenant</button>`}
    <//>

    <div className="grid g2 sa-tenants-desk__grid">
      <${Card} title="User tree" sub="Isolation is parental — visibility follows the tree. Brochure only — never writes.">
        <div className="stream" style=${{ paddingLeft: 26 }}>
          ${[
            ["superadmin", "Exactly one root SA. Global platform control. Never created by public signup."],
            ["sa_staff", "Delegated operators of that one SA — capability-scoped and time-boxed. Not a second SA."],
            ["admin", "Tenant under SA. Owns devices, users, groups and settings. Child admins sit under a parent admin."],
            ["user", "Under an admin. Sees what is shared. Cannot send commands."],
          ].map(([k, v], i) => html`
            <div key=${k} className="stage" data-done="1" style=${{ paddingBottom: 16 }}>
              <span className="stage__node" style=${{ fontSize: "var(--t-micro)" }}>${i}</span>
              <div className="stage__h"><b>${k}</b></div>
              <p className="stage__say">${v}</p>
            </div>`)}
        </div>
        <p className="note">A child administrator sees only devices shared to their whitelisted users.
          A device the parent owns but has not shared is invisible — Soft cross-tenant lists are forbidden.</p>
      <//>
      <${Card} title="Device custody" meta="sa_device_admin"
          sub="Cross-tenant moves are SA-scoped. Close tenant is on this desk; transfer/reclaim stay on Custody.">
        <p className="sub">Transfer and reclaim need an explicit write with an audit row on Custody.</p>
        <button className="btn btn--ghost btn--sm" type="button"
          onClick=${() => go("/sa/custody")}>Open Custody desk</button>
      <//>
    </div>
  </div>`;
}

export function Tenants({ toast }) {
  return html`<${F} data-cmc-page="page81-sa-tenants-mount">
    <${SaTenantsDesk} toast=${toast}/>
  <//>`;
}
